import { Controller } from '@hotwired/stimulus'
import type { ECharts } from 'echarts/core'
import type { EChartsOption } from 'echarts'
import SFetch from '@app/utils/sfetch'
import { SfLog } from '@app/adapters/logger'
import { EchartsHost } from '@app/shared/echarts/echarts-host'
import {
  SF_NEUTRAL_DARKER_1,
  SF_NEUTRAL_LIGHT_2,
} from '@app/shared/echarts/echarts-colors'

interface PulseDataPoint {
  date: string
  value: number
  formattedValue: string
  formattedDate: string
}

const BAR_COLOR = SF_NEUTRAL_LIGHT_2
const BAR_HIGHLIGHT_COLOR = SF_NEUTRAL_DARKER_1
const MIN_OPACITY = 0.7
const ZERO_LINE_COLOR = '#ccc'
// $grey in the ui-kit, lighter than the echarts default label colour.
const AXIS_LABEL_COLOR = '#9b9b9b'

// Renders a pulse-formula trend bar chart. Hovering a column updates the
// sibling value and period labels, and a click pins the column.
export default class extends Controller {
  static targets = ['chart', 'value', 'period']
  static values = { url: String }

  declare readonly chartTarget: HTMLElement
  declare readonly valueTarget: HTMLElement
  declare readonly hasValueTarget: boolean
  declare readonly periodTarget: HTMLElement
  declare readonly hasPeriodTarget: boolean
  declare urlValue: string

  private host: EchartsHost | null = null
  private dataPoints: PulseDataPoint[] = []
  private pinnedIndex: number | null = null

  private get chart(): ECharts | null {
    return this.host?.instance ?? null
  }

  connect(): void {
    this.pinnedIndex = null
    // Fresh host per connection, captured by render() so an in-flight render
    // stays bound to the host disconnect() disposes, even across a reconnect.
    const host = new EchartsHost()
    this.host = host
    void this.render(host)
  }

  disconnect(): void {
    this.host?.dispose()
    this.host = null
  }

  private async render(host: EchartsHost): Promise<void> {
    if (!this.urlValue) return

    try {
      const data = await SFetch.json<{ dataPoints: PulseDataPoint[] }>(
        this.urlValue
      )
      if (host !== this.host) return
      this.dataPoints = data?.dataPoints || []
      if (this.dataPoints.length === 0) return

      // mount() returns null when disconnect() ran during the fetch or the chunk
      // load. The hook shows the chart, hidden while loading, before echarts
      // measures it.
      const chart = await host.mount(this.chartTarget, () =>
        this.element.classList.remove('pulse-formula-loading')
      )
      if (!chart) return

      chart.setOption(this.buildOption())

      const zr = chart.getZr()
      zr.on('mousemove', (event) => this.handleHover(event.offsetX))
      zr.on('mouseout', () => this.handleHoverOut())
      zr.on('click', (event) => this.handleClick(event.offsetX))
    } catch (error) {
      SfLog.warn('Failed to render pulse chart', { error })
    }
  }

  private buildOption(): EChartsOption {
    const values = this.dataPoints.map((point) => point.value)
    const maxAbs = Math.max(...values.map(Math.abs), 0) || 1
    // Ticks only at the data extent, which always includes 0 so the baseline
    // shows, instead of the rounded 0, 500K, 1M ticks echarts picks.
    const axisMax = Math.max(0, ...values)
    const axisMin = Math.min(0, ...values)
    const compact = new Intl.NumberFormat(
      window.Silverfin.currentUserLocale() || undefined,
      { notation: 'compact', maximumSignificantDigits: 2 }
    )

    return {
      grid: { left: 40, right: 0, top: 5, bottom: 5, containLabel: false },
      xAxis: {
        type: 'category',
        data: this.dataPoints.map((point) => point.date),
        axisLine: { onZero: true, lineStyle: { color: ZERO_LINE_COLOR } },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      yAxis: {
        type: 'value',
        min: axisMin,
        max: axisMax,
        // A single interval spanning the whole range yields just the two
        // extreme ticks (min and max), with no intermediate 0/round values.
        interval: axisMax - axisMin || undefined,
        splitLine: { show: false },
        axisLabel: {
          color: AXIS_LABEL_COLOR,
          fontSize: 11,
          formatter: (value: number) => compact.format(value),
        },
      },
      series: [
        {
          type: 'bar',
          itemStyle: { color: BAR_COLOR, borderRadius: 2 },
          emphasis: { itemStyle: { color: BAR_HIGHLIGHT_COLOR, opacity: 1 } },
          data: this.dataPoints.map((point) => ({
            value: point.value,
            itemStyle: {
              opacity:
                MIN_OPACITY +
                (1 - MIN_OPACITY) * (Math.abs(point.value) / maxAbs),
            },
          })),
        },
      ],
    }
  }

  private indexForX(pixelX: number): number | null {
    if (!this.chart) return null
    const raw = this.chart.convertFromPixel({ xAxisIndex: 0 }, pixelX)
    const index = Math.round(Number(raw))
    if (!Number.isInteger(index)) return null
    if (index < 0 || index >= this.dataPoints.length) return null
    return index
  }

  private handleHover(pixelX: number): void {
    const index = this.indexForX(pixelX)
    if (index === null) return
    this.showDetails(index)
  }

  private handleHoverOut(): void {
    this.resetDetails()
    if (this.pinnedIndex !== null) this.showDetails(this.pinnedIndex)
  }

  private handleClick(pixelX: number): void {
    const index = this.indexForX(pixelX)
    if (index === null || index === this.pinnedIndex) {
      this.pinnedIndex = null
      this.resetDetails()
      return
    }
    this.pinnedIndex = index
    this.showDetails(index)
  }

  private showDetails(index: number): void {
    const point = this.dataPoints[index]
    if (!point || !this.chart) return

    this.chart.dispatchAction({ type: 'downplay', seriesIndex: 0 })
    this.chart.dispatchAction({
      type: 'highlight',
      seriesIndex: 0,
      dataIndex: index,
    })

    if (this.hasValueTarget) this.valueTarget.textContent = point.formattedValue
    if (this.hasPeriodTarget) {
      this.periodTarget.textContent = `${point.formattedDate} PTD`
    }
  }

  private resetDetails(): void {
    this.chart?.dispatchAction({ type: 'downplay', seriesIndex: 0 })

    if (this.hasValueTarget && this.valueTarget.dataset.original) {
      this.valueTarget.textContent = this.valueTarget.dataset.original
    }
    if (this.hasPeriodTarget && this.periodTarget.dataset.original) {
      this.periodTarget.textContent = this.periodTarget.dataset.original
    }
  }
}
